import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { mapValues } from 'lodash';

import { showToast } from '@domains/pdp-common';
import { PDP_INTEGRATED_DATA_QUERY_KEY } from '@domains/pdp-common';
import {
  SizeRecommendation,
  useCatalogProductIdParams,
  useGetPdpIntegratedData,
  useIsAuthenticated,
} from '@domains/pdp-common';

import { useSizeInfoLoginRedirect } from '../../size-info/hooks/useSizeInfoLoginRedirect';
import { usePendingSizeInput } from '../../size-info/queries/usePendingSizeInputQuery';
import { SetUserAccountBodyInput } from '../graphql/user-body-input_types.2';
import { toSizeRecommendation } from '../mappers/toSizeRecommendation';
import { mapSelectionListToComponent } from '../mappers/toUserBodyInputComponents';
import { useSetUserAccountBodyMutation } from '../queries/useSetUserAccountBodyMutation';
import { UserBodySelectionInputInfoModel } from '../utils/types';

/**
 * 사이즈 추천 컴포넌트 노출 여부를 결정합니다.
 *
 * 서버에서 아래 조건에 해당하면 size_recommendation 데이터를 내려주지 않습니다 (null).
 *
 * [미노출 조건]
 * 1. 복수옵션 여부 N
 * 2. base_id 1 (구매 비중 기반) 또는 base_id 2 (옵션값별 최빈 구매 체형 기반)
 * 3. 위 조건에 해당하는 추천 사이즈 유무 N일 경우
 */
const shouldShowSizeRecommendation = (sizeRecommendation?: SizeRecommendation | null) => {
  return Boolean(sizeRecommendation);
};

export const useSizeRecommendationService = (propsCatalogProductId?: string) => {
  const queryClient = useQueryClient();
  const { data: pendingData, isLoading: isPendingLoading, setPending } = usePendingSizeInput();
  const { catalog_product_id: paramsCatalogProductId } = useCatalogProductIdParams();
  const catalog_product_id = propsCatalogProductId || paramsCatalogProductId;
  const [values, setValues] = useState<Record<string, number | string | string[] | number[] | null>>({});
  const { loginWithRedirect } = useSizeInfoLoginRedirect({ shouldSetPopupFlag: true });
  const { isLoggedIn: isAuthenticated, isLoading: isAuthLoading } = useIsAuthenticated();

  const { mutateAsync: setUserAccountBody, isLoading: isLoadingSetUserAccountBody } = useSetUserAccountBodyMutation();

  const {
    size_recommendation,
    refetch,
    isLoading: isLoadingIntegratedData,
  } = useGetPdpIntegratedData({
    catalog_product_id,
  });

  const sizeRecommendation = useMemo(() => toSizeRecommendation(size_recommendation), [size_recommendation]);

  const components = useMemo(() => {
    if (!sizeRecommendation.userBodyInputInfo?.selectionList) {
      return [];
    }

    return sizeRecommendation.userBodyInputInfo.selectionList.map((item) => {
      const value = (values[item.key] as number) || null;
      return mapSelectionListToComponent(item, value);
    });
  }, [sizeRecommendation.userBodyInputInfo, values]);

  const handleChange = (key: string, value: number | string | string[] | number[] | null) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const save = async (inputValues: Record<string, any>) => {
    try {
      const input = mapValues(inputValues, Number) as unknown as SetUserAccountBodyInput;

      await setUserAccountBody({
        variables: {
          input,
        },
      });

      await queryClient.invalidateQueries([PDP_INTEGRATED_DATA_QUERY_KEY, catalog_product_id]);
      showToast('체형 정보가 저장되었습니다.');
      return true;
    } catch (error) {
      showToast('체형 정보 저장에 실패했습니다.');
      return false;
    }
  };

  const submit = async () => {
    const selectionList = sizeRecommendation.userBodyInputInfo?.selectionList || [];
    const isAllSelected =
      selectionList.length > 0 &&
      selectionList.every((item: UserBodySelectionInputInfoModel) => {
        const value = values[item.key];
        return value !== undefined && value !== null && value !== '';
      });

    if (!isAllSelected) {
      showToast('체형을 선택해 주세요');
      return;
    }

    if (isAuthenticated === false) {
      setPending(values);
      loginWithRedirect(catalog_product_id);
      return;
    }

    await save(values);
  };

  useEffect(() => {
    if (isAuthLoading || isPendingLoading) return;

    const pendingValues = pendingData ?? null;

    if (isAuthenticated && pendingValues) {
      setValues(pendingValues);
      return;
    }

    if (pendingValues) return;

    if (
      Object.keys(values).length === 0 &&
      sizeRecommendation.userAccount?.body &&
      sizeRecommendation.userAccount.body.height > 0 &&
      sizeRecommendation.userAccount.body.weight > 0
    ) {
      setValues({
        height: sizeRecommendation.userAccount.body.height,
        weight: sizeRecommendation.userAccount.body.weight,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isAuthLoading, isPendingLoading, pendingData, catalog_product_id]);

  return {
    loading: isLoadingSetUserAccountBody,
    isLoadingIntegratedData,
    catalogProductId: catalog_product_id,
    visible: shouldShowSizeRecommendation(size_recommendation),
    sizeRecommendation,
    recommendOptionValueList: sizeRecommendation.recommendOptionValueList,
    recommendationType: sizeRecommendation.recommendationType,
    userAccount: sizeRecommendation.userAccount,
    userBodyInputInfo: sizeRecommendation.userBodyInputInfo,
    isReviewButtonVisible: sizeRecommendation.isReviewButtonVisible,
    refetch,
    // User Body Input Data
    title: sizeRecommendation.userBodyInputInfo?.htmlTitle || '',
    components,
    ubl: {
      id: 'apply',
      type: 'button',
      section: 'my_size_filter',
      __typename: 'UxUblObject',
    },
    // Handlers
    handleChange,
    submit,
    setPending,
  };
};
